import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { BellOff, CheckCheck, MoreVertical, RefreshCw, Trash2 } from 'lucide-react';
import { storageManager, ListingNotification } from '../services/StorageManager';
import { ListingCard } from './ListingCard';
import { ConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { monthlyFromLabel } from '../config/plans';
import { cn } from '../utils/cn';
import { Banner, Button, buttonClasses, EmptyState, IconButton, Menu, PageHeader, SegmentedControl } from './ui';

export const UnifiedFeed: React.FC = () => {
    const { user } = useAuth();
    const { openUpgradeModal } = useUpgradeModal();
    const [notifications, setNotifications] = useState<ListingNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
        return (localStorage.getItem('feed_view_mode') as 'card' | 'list') || 'card';
    });
    const [isIOS, setIsIOS] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
    const location = useLocation();
    const visibilityTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const selectionTimer = useRef<NodeJS.Timeout | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        const data = await storageManager.getNotifications(500);
        // Apply feed max-age filter from localStorage preference
        const maxAgeDays = (() => {
            const stored = localStorage.getItem('feed_max_age_days');
            return stored !== null ? parseInt(stored, 10) : 0;
        })();
        const filtered = maxAgeDays > 0
            ? data.filter(n => n.timestamp >= Date.now() - maxAgeDays * 86400000)
            : data;
        setNotifications(filtered);
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();

        // Instant updates via BroadcastChannel
        const channel = new BroadcastChannel('feed_updates');
        channel.onmessage = (event) => {
            console.log('[UnifiedFeed] Received real-time update:', event.data);
            loadData();
        };

        // Handle Service Worker messages (e.g., notification clicks)
        const swMessageHandler = (event: MessageEvent) => {
            if (event.data) {
                if (event.data.type === 'SELECT_LISTING') {
                    console.log('[UnifiedFeed] Selected listing via SW:', event.data.id);
                    setSelectedId(event.data.id);
                    // Update URL without reload to match the selection
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('select', event.data.id);
                    window.history.pushState({}, '', newUrl.toString());

                    // Selection should vanish after 5 seconds
                    if (selectionTimer.current) clearTimeout(selectionTimer.current);
                    selectionTimer.current = setTimeout(() => {
                        console.log('[UnifiedFeed] Selection vanishing');
                        setSelectedId(null);
                        const cleanUrl = new URL(window.location.href);
                        cleanUrl.searchParams.delete('select');
                        window.history.pushState({}, '', cleanUrl.toString());
                    }, 5000);
                } else if (event.data.type === 'RESET_SELECTION') {
                    console.log('[UnifiedFeed] Resetting selection via SW');
                    setSelectedId(null);
                    // Clear selection from URL without reload
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('select');
                    window.history.pushState({}, '', newUrl.toString());
                    if (selectionTimer.current) clearTimeout(selectionTimer.current);
                } else if (event.data.type === 'REFRESH_FEED') {
                    console.log('[UnifiedFeed] Received REFRESH_FEED message from SW');
                    loadData();
                } else if (event.data.type === 'ALL_READ' || event.data.type === 'ALL_DELETED') {
                    loadData();
                }
            }
        };

        // Handle visibility changes (e.g., returning to the app)
        const visibilityHandler = () => {
            if (document.visibilityState === 'visible') {
                console.log('[UnifiedFeed] Document became visible, refreshing data');
                loadData();
            }
        };

        document.addEventListener('visibilitychange', visibilityHandler);

        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', swMessageHandler);
        }

        // Rely solely on BroadcastChannel and Service Worker messages for updates.
        // Polling (setInterval) has been removed to save resources.

        // Detect iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        const ios = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(ios);

        return () => {
            channel.close();
            document.removeEventListener('visibilitychange', visibilityHandler);
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', swMessageHandler);
            }
            if (selectionTimer.current) clearTimeout(selectionTimer.current);
        };
    }, [loadData]);

    // Handle initial selection from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const selectId = params.get('select');
        if (selectId) {
            setSelectedId(selectId);
            // Selection should vanish after 5 seconds
            if (selectionTimer.current) clearTimeout(selectionTimer.current);
            selectionTimer.current = setTimeout(() => {
                console.log('[UnifiedFeed] Selection vanishing (initial)');
                setSelectedId(null);
                const cleanUrl = new URL(window.location.href);
                cleanUrl.searchParams.delete('select');
                window.history.pushState({}, '', cleanUrl.toString());
            }, 5000);
        }
    }, [location.search]);

    // Scroll to selected item when data is loaded
    useEffect(() => {
        if (!loading && selectedId && notifications.length > 0) {
            const element = document.getElementById(`listing-${selectedId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [loading, selectedId, notifications]);

    // Auto-mark as read when visible for 3 seconds
    useEffect(() => {
        // Cleanup function
        const cleanup = () => {
            visibilityTimers.current.forEach(timer => clearTimeout(timer));
            visibilityTimers.current.clear();
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };

        if (loading || notifications.length === 0) {
            cleanup();
            return;
        }

        // Create intersection observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const listingId = entry.target.getAttribute('data-listing-id');
                    if (!listingId) return;

                    const notification = notifications.find(n => n.id === listingId);
                    if (!notification || notification.read) return;

                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        // Listing is visible, start timer if not already started
                        if (!visibilityTimers.current.has(listingId)) {
                            const timer = setTimeout(async () => {
                                await storageManager.markAsRead(listingId);
                                setNotifications(prev => prev.map(n =>
                                    n.id === listingId ? { ...n, read: true } : n
                                ));
                                visibilityTimers.current.delete(listingId);

                                // Try to close notification if service worker is available
                                if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                                    navigator.serviceWorker.controller.postMessage({
                                        type: 'CLOSE_NOTIFICATION',
                                        listingId: listingId
                                    });
                                }
                            }, 3000);
                            visibilityTimers.current.set(listingId, timer);
                        }
                    } else {
                        // Listing is not visible, clear timer
                        const timer = visibilityTimers.current.get(listingId);
                        if (timer) {
                            clearTimeout(timer);
                            visibilityTimers.current.delete(listingId);
                        }
                    }
                });
            },
            { threshold: 0.5 } // 50% visible
        );

        // Observe all unread listings
        notifications.forEach(notification => {
            if (!notification.read) {
                const element = document.querySelector(`[data-listing-id="${notification.id}"]`);
                if (element && observerRef.current) {
                    observerRef.current.observe(element);
                }
            }
        });

        return cleanup;
    }, [loading, notifications]);

    const handleItemClick = async (notification: ListingNotification) => {
        if (!notification.read) {
            await storageManager.markAsRead(notification.id);
            // Optimistic update
            setNotifications(prev => prev.map(n =>
                n.id === notification.id ? { ...n, read: true } : n
            ));
        }

        if (notification.url) {
            window.open(notification.url, '_blank');
        }
    };

    const handleDelete = async (notification: ListingNotification) => {
        await storageManager.deleteNotification(notification.id);
        // Optimistic update
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
    };

    const handleMarkAllRead = async () => {
        await storageManager.markAllAsRead();
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const handleClearAll = async () => {
        setShowClearAllConfirm(false);
        await storageManager.deleteAllNotifications();
        setNotifications([]);
    };

    const handleViewModeChange = (mode: 'card' | 'list') => {
        setViewMode(mode);
        localStorage.setItem('feed_view_mode', mode);
    };

    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        const minDelay = new Promise(resolve => setTimeout(resolve, 500));
        try {
            await Promise.all([loadData(), minDelay]);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-wide">
            {/* No "Anmelden" action here for anonymous visitors — AppShell's header (allowAnonymous)
                already renders one; a second copy duplicated it on /feed (M6). */}
            <PageHeader title="Treffer" description="Alle Meldungen deiner Suchen an einem Ort." />

            {notifications.length > 0 && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
                    <div className="flex flex-wrap items-center gap-2">
                        {isIOS && (
                            <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} aria-hidden="true" />}
                                onClick={handleManualRefresh}
                                disabled={isRefreshing}
                            >
                                Aktualisieren
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<CheckCheck className="h-4 w-4" aria-hidden="true" />}
                            onClick={handleMarkAllRead}
                            disabled={!notifications.some((n) => !n.read)}
                        >
                            Alle als gelesen markieren
                        </Button>
                        <Menu
                            align="start"
                            trigger={(props) => (
                                <IconButton {...props} label="Weitere Aktionen" icon={<MoreVertical className="h-4 w-4" />} variant="ghost" size="sm" />
                            )}
                            items={[
                                {
                                    label: 'Alle Treffer löschen',
                                    danger: true,
                                    icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                                    onSelect: () => setShowClearAllConfirm(true),
                                },
                            ]}
                        />
                    </div>
                    <SegmentedControl
                        label="Ansicht"
                        showLabel={false}
                        size="sm"
                        value={viewMode}
                        onChange={handleViewModeChange}
                        options={[
                            { value: 'card', label: 'Karten' },
                            { value: 'list', label: 'Liste' },
                        ]}
                    />
                </div>
            )}

            <div className="mt-4">
                {loading && notifications.length === 0 ? (
                    <p className="py-12 text-center text-muted">Treffer werden geladen...</p>
                ) : notifications.length === 0 ? (
                    <EmptyState
                        icon={<BellOff className="h-6 w-6" aria-hidden="true" />}
                        title="Noch keine Treffer"
                        action={
                            <Link to="/dashboard" state={{ noRedirect: true }} className={buttonClasses('secondary')}>
                                Zu deinen Suchen
                            </Link>
                        }
                    >
                        Sobald eine deiner Suchen etwas findet, erscheint es hier und als Meldung auf deinem Handy.
                    </EmptyState>
                ) : (
                    <div className="flex flex-col gap-4">
                        {notifications.map((notification, index) => (
                            <React.Fragment key={notification.id}>
                                <div
                                    id={`listing-${notification.id}`}
                                    data-listing-id={notification.id}
                                    className={notification.id === selectedId ? 'selected-listing-wrapper' : undefined}
                                >
                                    <ListingCard
                                        notification={notification}
                                        viewMode={viewMode}
                                        onClick={handleItemClick}
                                        onDelete={handleDelete}
                                    />
                                </div>
                                {user?.tier === 'free' && index === 4 && notifications.length > 5 && (
                                    <Banner
                                        tone="info"
                                        action={
                                            <Button type="button" variant="secondary" size="sm" onClick={() => openUpgradeModal('pro')}>
                                                Pro ansehen
                                            </Button>
                                        }
                                    >
                                        Mit Pro siehst du alle Treffer vollständig — {monthlyFromLabel('pro')}.
                                    </Banner>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>

            {showClearAllConfirm && (
                <ConfirmDialog
                    title="Alle Treffer löschen?"
                    message="Diese Aktion kann nicht rückgängig gemacht werden."
                    confirmLabel="Ja, löschen"
                    cancelLabel="Abbrechen"
                    danger
                    onConfirm={handleClearAll}
                    onCancel={() => setShowClearAllConfirm(false)}
                />
            )}
        </div>
    );
};
